import cardValidator from 'card-validator';
import { normalizeCardNumber } from '@src/domains/payments/core/normalizeCardNumber';
import type { PaymentOption } from '@src/domains/payments/types/payment';

// card-validator names some brands differently from the brand codes the backend sends
// (core/internal/domain/payment_card.go:94). Divergences of name go here, so identifying a brand
// stays a lookup rather than a chain of ifs.
const BRAND_CODE_BY_VALIDATOR_TYPE: Record<string, string> = {
  'american-express': 'amex',
  'diners-club': 'diners',
};

// Aura diverges by rule, not by name: card-validator has no aura type and reads core's aura numbers
// (payment_card.go:198, `^50\d{14}$`) as maestro, so only that exact shape becomes aura. A real
// Maestro — 6759, 676x — is a brand core cannot name, and has to stay refused here rather than fail
// at the gateway.
const AURA_BRAND_CODE = 'aura';
const AURA_VALIDATOR_TYPE = 'maestro';
const AURA_NUMBER = /^50\d{14}$/;

const resolveBrandCode = (validatorType: string, digits: string): string => {
  if (validatorType === AURA_VALIDATOR_TYPE && AURA_NUMBER.test(digits)) {
    return AURA_BRAND_CODE;
  }

  return BRAND_CODE_BY_VALIDATOR_TYPE[validatorType] ?? validatorType;
};

export const isAcceptedCardBrand = (value: string, paymentOption?: PaymentOption): boolean => {
  const cardInfo = cardValidator.number(value);

  if (cardInfo.card === null) {
    return false;
  }

  const brand = resolveBrandCode(cardInfo.card.type, normalizeCardNumber(value));

  return Boolean(paymentOption?.brands?.map((b) => b.code).includes(brand));
};
